// src/layouts/AdminLayout.js
//
// FA-23/39: sticky sidebar on desktop, accessible drawer on phones (Esc closes it,
// labelled buttons), `min-w-0` main column so wide tables scroll inside the page
// instead of stretching it. Renders `children`, or the nested route (<Outlet/>)
// when used as a layout route.
import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import AdminSidebar from '../components/layout/AdminSidebar';
import Navbar from '../components/layout/Navbar';

export const AdminLayout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();

  // Close the phone drawer after navigation and on Esc.
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isSidebarOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setIsSidebarOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isSidebarOpen]);

  return (
    <div className="min-h-screen bg-gray-100 print:min-h-0 print:bg-white">
      <div className="print:hidden">
        <Navbar>
          <button
            type="button"
            onClick={() => setIsSidebarOpen((open) => !open)}
            aria-label={isSidebarOpen ? 'Zavřít menu' : 'Otevřít menu'}
            aria-expanded={isSidebarOpen}
            aria-controls="admin-sidebar"
            className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 md:hidden"
          >
            <Menu className="h-6 w-6" aria-hidden="true" />
          </button>
        </Navbar>
      </div>

      <div className="flex">
        {/* Phone: drawer over the page. Desktop: sticky column. */}
        <div
          className={`${isSidebarOpen ? 'block' : 'hidden'} fixed inset-0 z-40 md:sticky md:top-0 md:z-0 md:block md:h-screen md:flex-shrink-0 md:self-start print:hidden`}
        >
          <div className="absolute inset-0 bg-gray-600/75 md:hidden" onClick={() => setIsSidebarOpen(false)} aria-hidden="true" />
          <AdminSidebar id="admin-sidebar" onClose={() => setIsSidebarOpen(false)} />
        </div>

        <main className="w-full min-w-0 flex-1 p-4 sm:p-6 print:m-0 print:p-0">
          <div className="mx-auto max-w-7xl print:m-0 print:max-w-none">{children ?? <Outlet />}</div>
        </main>
      </div>
    </div>
  );
};
